// Cotizaciones (fase B): estados, líneas, filas que se guardan, mensaje de WhatsApp y documento imprimible.
// El cálculo es el de src/utils/billing.js (el mismo de la base de datos). Diseño: docs/estrategia/MODULO_FACTURACION.md
import { calcDocument, calcLine } from './billing';
import { addDaysStr, isLidOnly, localDateStr, phoneDigits } from './customers';

export const STATUS_LABELS = {
  borrador: 'Borrador', aprobada: 'Aprobada', enviada: 'Enviada', aceptada: 'Aceptada',
  rechazada: 'Rechazada', vencida: 'Vencida', reemplazada: 'Reemplazada'
};

export const STATUS_TONES = {
  borrador: 'bg-slate-500/20 text-slate-200 border-slate-500/40',
  aprobada: 'bg-sky-500/20 text-sky-200 border-sky-500/40',
  enviada: 'bg-amber-500/20 text-amber-200 border-amber-500/40',
  aceptada: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40',
  rechazada: 'bg-red-500/20 text-red-200 border-red-500/40',
  vencida: 'bg-orange-500/20 text-orange-200 border-orange-500/40',
  reemplazada: 'bg-slate-700/40 text-slate-400 border-slate-600/40'
};

export const TAX_KIND_LABELS = { iva: 'IVA', consumo: 'Impuesto al consumo', excluido: 'Excluido', exento: 'Exento' };

// Pesos sin decimales cuando el valor es entero; con dos cuando hay centavos ($107,10, no $107,1).
export const money = (n) => {
  const v = Number(n ?? 0);
  return `$${v.toLocaleString('es-CO', { minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2 })}`;
};

export const DEFAULT_SETTINGS = {
  currency: 'COP', quote_valid_days: 15, quote_prefix: 'COT', quote_suffix: '', sale_prefix: 'VTA', sale_suffix: '',
  legal_name: '', tax_id: '', address: '', phone: '', email: '', footer_text: '',
  auto_approve: false, auto_approve_max_amount: null, stock_policy: 'warn', auto_send_on_approval: false, notify_phone: ''
};

// Una enviada cuya fecha de vigencia ya pasó se muestra como vencida (el cambio de estado lo confirma la persona).
export const isExpired = (quote, today = localDateStr()) =>
  quote?.status === 'enviada' && Boolean(quote.valid_until) && String(quote.valid_until).slice(0, 10) < today;

export const shownStatus = (quote, today = localDateStr()) => (isExpired(quote, today) ? 'vencida' : quote?.status);

// La que arma el bot espera revisión humana mientras siga en borrador.
export const needsReview = (quote) => quote?.source === 'bot' && quote?.status === 'borrador';

// La base la aprobó sola por la regla del dueño (tope), no una persona: aprobada por "sistema".
export const autoApproved = (quote) => quote?.status === 'aprobada' && quote?.approved_by === 'sistema';
export const approverLabel = (approvedBy) => (approvedBy === 'sistema' ? 'la regla de aprobación automática' : approvedBy);

export const newId = () =>
  (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r & 3) | 8).toString(16);
  }));

// Línea nueva a partir de un producto del catálogo. El precio sale de la lista del cliente; si el producto no la tiene, de la predeterminada
// (la línea lo marca) y, si tampoco, queda en 0 marcada "sin precio" para que la persona lo escriba. Precio, descuento e impuesto quedan congelados.
export const lineFromProduct = (product, { priceMap, listKey, defaultKey, position = 0 }) => {
  const own = priceMap?.get(product.id) || {};
  const exact = own[listKey];
  const price = exact ?? own[defaultKey];
  return {
    id: newId(), product_id: product.id, sku: product.sku || null, description: product.name, unit: product.unit || null,
    quantity: 1, unit_price: price ?? 0, discount_pct: 0,
    tax_rate: Number(product.tax_rate || 0), tax_kind: product.tax_kind || 'iva', position,
    fallbackPrice: price !== undefined && exact === undefined, noPrice: price === undefined
  };
};

export const blankLine = (position = 0) => ({
  id: newId(), product_id: null, sku: null, description: '', unit: null, quantity: 1, unit_price: 0,
  discount_pct: 0, tax_rate: 0, tax_kind: 'iva', position
});

// Revisa cada línea y calcula el documento. Devuelve los errores por línea (para marcarlos) en vez de lanzar.
export const computeDocument = (lines) => {
  const errors = {};
  const valid = [];
  (lines || []).forEach((line, i) => {
    if (!String(line.description ?? '').trim()) { errors[i] = 'Escribe la descripción.'; return; }
    try { calcLine(line); valid.push(line); } catch (e) { errors[i] = e instanceof RangeError ? `Revisa los valores: ${e.message}.` : 'Revisa los valores.'; }
  });
  const ok = Object.keys(errors).length === 0;
  const totals = calcDocument(valid);
  return { ok, errors, totals, lineTotals: (lines || []).map((l, i) => (errors[i] ? null : calcLine(l).total)) };
};

// Existencias para avisar en la pantalla (no se descuentan al cotizar). Devuelve las líneas cuyo producto tiene menos de lo pedido.
export const stockWarnings = (lines, productsById) => {
  const need = new Map();
  (lines || []).forEach((l) => { if (l.product_id) need.set(l.product_id, (need.get(l.product_id) || 0) + Number(l.quantity || 0)); });
  const out = [];
  need.forEach((qty, id) => {
    const p = productsById?.get(id);
    if (p && p.stock !== null && p.stock !== undefined && Number(p.stock) < qty) out.push({ product_id: id, name: p.name, stock: Number(p.stock), needed: qty });
  });
  return out;
};

// Columnas que la base deja escribir a la app (coinciden con los GRANT de 20261003b_billing_base.sql; hay una prueba que lo verifica).
export const QUOTE_INSERT_COLUMNS = ['id', 'business_id', 'replaces_id', 'customer_id', 'price_list_key', 'source', 'valid_until', 'notes'];
export const QUOTE_UPDATE_COLUMNS = ['customer_id', 'price_list_key', 'status', 'valid_until', 'notes'];
export const ITEM_INSERT_COLUMNS = ['id', 'quote_id', 'business_id', 'product_id', 'sku', 'description', 'unit', 'quantity', 'unit_price', 'discount_pct', 'tax_rate', 'tax_kind', 'position'];
export const ITEM_UPDATE_COLUMNS = ['product_id', 'sku', 'description', 'unit', 'quantity', 'unit_price', 'discount_pct', 'tax_rate', 'tax_kind', 'position'];

const pick = (obj, cols) => Object.fromEntries(cols.map((c) => [c, obj[c]]));
const num = (v) => (v === '' || v === null || v === undefined ? v : Number(v));

const itemValues = (line, position) => ({
  product_id: line.product_id || null, sku: line.sku || null, description: String(line.description).trim().slice(0, 300),
  unit: line.unit || null, quantity: num(line.quantity), unit_price: num(line.unit_price), discount_pct: num(line.discount_pct || 0),
  tax_rate: num(line.tax_rate || 0), tax_kind: line.tax_kind || 'iva', position
});

export const itemInsertRow = (line, quoteId, businessId, position) =>
  pick({ id: line.id, quote_id: quoteId, business_id: businessId, ...itemValues(line, position) }, ITEM_INSERT_COLUMNS);

export const itemUpdateRow = (line, position) => pick(itemValues(line, position), ITEM_UPDATE_COLUMNS);

// ¿Cambió la línea respecto a lo guardado? (comparando números como números, para no reescribir "5" contra 5.00)
export const lineChanged = (line, saved, position) => {
  const next = itemValues(line, position);
  return ITEM_UPDATE_COLUMNS.some((c) => {
    const a = next[c]; const b = saved[c];
    if (['quantity', 'unit_price', 'discount_pct', 'tax_rate', 'position'].includes(c)) return Number(a) !== Number(b);
    return (a ?? null) !== (b ?? null);
  });
};

// Qué hay que escribir para llevar las líneas guardadas a las de la pantalla: nuevas, cambiadas y borradas.
export const diffItems = (savedItems, lines) => {
  const savedById = new Map((savedItems || []).map((i) => [i.id, i]));
  const keep = new Set();
  const toInsert = []; const toUpdate = [];
  lines.forEach((line, position) => {
    const saved = savedById.get(line.id);
    if (!saved) { toInsert.push({ line, position }); return; }
    keep.add(line.id);
    if (lineChanged(line, saved, position)) toUpdate.push({ id: line.id, row: itemUpdateRow(line, position) });
  });
  const toDelete = (savedItems || []).filter((i) => !keep.has(i.id)).map((i) => i.id);
  return { toInsert, toUpdate, toDelete };
};

// ---- Fechas y teléfonos ----
// "2026-10-03" (fecha sola, sin zona) se muestra tal cual; una marca de tiempo (…T…Z) se pasa a la fecha local de quien la ve.
export const fmtDate = (value) => {
  if (!value) return '—';
  const raw = String(value);
  const only = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (only) return `${only[3]}/${only[2]}/${only[1]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  const [y, m, day] = localDateStr(d).split('-');
  return `${day}/${m}/${y}`;
};

// Fecha de vigencia que se fijará al enviar si la persona no puso una.
export const defaultValidUntil = (settings, now = new Date()) => addDaysStr(Number(settings?.quote_valid_days) || 15, now);

// wa.me necesita el indicativo del país: un celular colombiano de 10 dígitos (empieza por 3) se completa con 57.
export const waPhone = (customer) => {
  if (!customer || isLidOnly(customer)) return null;
  const d = phoneDigits(customer.phone);
  if (d.length === 10 && d.startsWith('3')) return `57${d}`;
  return d.length >= 7 ? d : null;
};

const MAX_WA_LINES = 12;

// Enlace público de la cotización (lo abre el cliente sin iniciar sesión): origen de la app + /cotizacion/<código>.
export const publicQuoteUrl = (origin, token) => (token ? `${String(origin || '').replace(/\/+$/, '')}/cotizacion/${token}` : '');

export const quoteWhatsappText = ({ quote, items, customer, business, settings, link }) => {
  const name = business?.nombre_negocio || settings?.legal_name || 'nuestro negocio';
  const greet = customer?.name ? `Hola ${String(customer.name).split(' ')[0]}` : 'Hola';
  const number = `${quote.number}${quote.version > 1 ? ` (versión ${quote.version})` : ''}`;
  const lines = (items || []).slice(0, MAX_WA_LINES).map((i) => `• ${Number(i.quantity)} ${i.unit || 'x'} ${i.description}: ${money(i.line_total)}`);
  if ((items || []).length > MAX_WA_LINES) lines.push(`…y ${(items || []).length - MAX_WA_LINES} más en el documento.`);
  return [
    `${greet}, te comparto la cotización ${number} de ${name}:`,
    '',
    ...lines,
    '',
    `Total: ${money(quote.total)} (${settings?.currency || 'COP'})`,
    quote.valid_until ? `Vigente hasta el ${fmtDate(quote.valid_until)}.` : '',
    link ? `Puedes verla y descargarla aquí: ${link}` : '',
    'Si estás de acuerdo, respóndeme por aquí y la dejamos lista. Quedo atento.'
  ].filter((l, i, arr) => !(l === '' && arr[i - 1] === '')).join('\n').trim();
};

// Estado del último envío por WhatsApp de una cotización, para mostrarlo en el editor.
// Número tal como lo arma la base: PREFIJO-0001 o PREFIJO-0001-SUFIJO (4 dígitos como mínimo). Sirve para mostrar cómo quedará.
export const formatDocNumber = (prefix, suffix, n) => {
  const num = Math.max(1, Math.floor(Number(n) || 1));
  return `${prefix || ''}-${num >= 10000 ? num : String(num).padStart(4, '0')}${suffix ? `-${suffix}` : ''}`;
};

// Avisos cuando el cliente pide un cambio (el estado de envío lo escribe la base y lo procesa n8n)
export const NOTIFY_LABELS = { pendiente: 'Aviso en cola', enviando: 'Avisando por WhatsApp', enviado: 'Aviso enviado por WhatsApp', error: 'No se pudo enviar el aviso', omitido: 'Sin aviso por WhatsApp (no hay número configurado)' };

export const DELIVERY_LABELS = { pendiente: 'En cola para enviarse', enviando: 'Enviándose', enviado: 'Enviada por WhatsApp', error: 'No se pudo enviar', cancelado: 'Envío cancelado' };
export const deliveryIsOpen = (d) => d?.status === 'pendiente' || d?.status === 'enviando';

export const whatsappUrl = (phone, text) => `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;

// ---- Documento imprimible (se abre en una ventana y se imprime o se guarda como PDF) ----
export const escapeHtml = (v) =>
  String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const nl2br = (v) => escapeHtml(v).replace(/\r?\n/g, '<br>');

const CSS = `
  *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:0;padding:32px;font-size:12px}
  h1{font-size:20px;margin:0} h2{font-size:13px;margin:0 0 4px} .muted{color:#555}
  .head{display:flex;justify-content:space-between;gap:24px;border-bottom:2px solid #111;padding-bottom:12px;margin-bottom:16px}
  .box{border:1px solid #bbb;border-radius:6px;padding:10px;margin-bottom:14px}
  table{width:100%;border-collapse:collapse;margin-bottom:12px} th{background:#eee;text-align:left;font-size:10px;text-transform:uppercase}
  th,td{padding:6px;border-bottom:1px solid #ddd;vertical-align:top} .r{text-align:right} .totals{margin-left:auto;width:280px}
  .totals td{border:0;padding:3px 6px} .grand td{font-size:15px;font-weight:bold;border-top:2px solid #111}
  .legal{margin-top:18px;font-size:10px;color:#555;border-top:1px solid #ccc;padding-top:8px}
  @media print{body{padding:0}}
  @media (max-width:600px){body{padding:12px;font-size:11px} .head{flex-direction:column;gap:8px} th,td{padding:4px 3px} th{font-size:9px} .totals{width:100%}}
`;

export const renderQuoteHtml = ({ quote, items, customer, business, settings }) => {
  const s = { ...DEFAULT_SETTINGS, ...(settings || {}) };
  const issuer = s.legal_name || business?.nombre_negocio || '';
  const cur = s.currency || 'COP';
  const number = `${quote.number}${quote.version > 1 ? ` · versión ${quote.version}` : ''}`;
  const c = customer || {};
  const fiscal = [c.legal_name || c.name, c.tax_id ? `NIT/CC: ${c.tax_id}` : '', c.billing_address, c.billing_city, c.billing_email || c.email, c.phone ? `Tel: +${c.phone}` : '']
    .filter(Boolean).map((x) => `<div>${escapeHtml(x)}</div>`).join('') || '<div class="muted">Sin cliente asignado</div>';
  const rows = (items || []).map((i, idx) => `
    <tr><td>${idx + 1}</td><td>${escapeHtml(i.sku || '')}</td><td>${escapeHtml(i.description)}</td>
    <td class="r">${escapeHtml(Number(i.quantity))} ${escapeHtml(i.unit || '')}</td><td class="r">${escapeHtml(money(i.unit_price))}</td>
    <td class="r">${Number(i.discount_pct) ? `${escapeHtml(Number(i.discount_pct))} %` : '—'}</td>
    <td class="r">${i.tax_kind === 'iva' || i.tax_kind === 'consumo' ? `${escapeHtml(Number(i.tax_rate))} %` : escapeHtml(TAX_KIND_LABELS[i.tax_kind] || '')}</td>
    <td class="r">${escapeHtml(money(i.line_total))}</td></tr>`).join('');
  return `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Cotización ${escapeHtml(quote.number)}</title><style>${CSS}</style></head><body>
  <div class="head">
    <div><h1>${escapeHtml(issuer)}</h1>
      ${[s.tax_id ? `NIT: ${s.tax_id}` : '', s.address, s.phone, s.email].filter(Boolean).map((x) => `<div class="muted">${escapeHtml(x)}</div>`).join('')}</div>
    <div class="r"><h1>COTIZACIÓN</h1><div><b>${escapeHtml(number)}</b></div>
      <div class="muted">Fecha: ${fmtDate(quote.sent_at || quote.created_at)}</div>
      <div class="muted">Vigente hasta: ${fmtDate(quote.valid_until)}</div></div>
  </div>
  <div class="box"><h2>Cliente</h2>${fiscal}</div>
  <table><thead><tr><th>#</th><th>Código</th><th>Descripción</th><th class="r">Cantidad</th><th class="r">Precio</th><th class="r">Desc.</th><th class="r">Impuesto</th><th class="r">Total</th></tr></thead>
    <tbody>${rows}</tbody></table>
  <table class="totals"><tbody>
    <tr><td>Subtotal</td><td class="r">${escapeHtml(money(quote.subtotal))}</td></tr>
    <tr><td>Descuentos</td><td class="r">−${escapeHtml(money(quote.discount_total))}</td></tr>
    <tr><td>Impuestos</td><td class="r">${escapeHtml(money(quote.tax_total))}</td></tr>
    <tr class="grand"><td>TOTAL (${escapeHtml(cur)})</td><td class="r">${escapeHtml(money(quote.total))}</td></tr>
  </tbody></table>
  ${quote.notes ? `<div class="box"><h2>Notas</h2><div>${nl2br(quote.notes)}</div></div>` : ''}
  ${s.footer_text ? `<div>${nl2br(s.footer_text)}</div>` : ''}
  <div class="legal">Este documento es una cotización y <b>no es una factura electrónica</b>. Los precios pueden cambiar después de la fecha de vigencia.</div>
  </body></html>`;
};
